import React, { useState, useEffect, useMemo, useRef } from 'react';
import { initializeApp, deleteApp } from 'firebase/app';
import { initializeAuth, inMemoryPersistence, createUserWithEmailAndPassword, signOut, sendPasswordResetEmail } from 'firebase/auth';
import { doc, setDoc, collection, onSnapshot, deleteDoc, updateDoc } from 'firebase/firestore';
import { db, auth, firebaseConfig } from '../lib/firebase';
import * as XLSX from 'xlsx';
import { UserPlus, Shield, User, Mail, Loader2, AlertCircle, CheckCircle, Trash2, Ban, UserCheck, RefreshCw, FileSpreadsheet, Clock, Copy, Check, X, Pencil, ArrowUp, ArrowDown, ArrowUpDown, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { getUserInitials, getUserAvatarColor } from '../utils/avatarUtils';
import { useAppContext } from '../context/AppContext';
import FluidTabs from './FluidTabs';
import { parseUserImportRows } from '../utils/userImport';

const USERS_PER_PAGE = 10;
type SortColumn = 'name' | 'email' | 'role' | 'assignedSalesPoint' | 'phone';
type SortDirection = 'asc' | 'desc';

const ROLE_LABELS: Record<UserItem['role'], string> = {
  designer: 'Tasarımcı',
  catalog_manager: 'Katalog Yöneticisi',
  admin: 'Yönetici',
};

interface UserItem {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'catalog_manager' | 'designer';
  disabled?: boolean;
  loginCount?: number;
  lastLoginAt?: string;
  assignedSalesPoint?: string;
  /** Contact phone shown on the proposal cover. Pre-filled from the assigned
   *  sales point's phone when the user is created, but freely editable here. */
  phone?: string;
}

/**
 * Generates a cryptographically secure temporary password using window.crypto.getRandomValues().
 */
function generateSecurePassword(length: number = 14): string {
  const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lowercase = 'abcdefghijkmnopqrstuvwxyz';
  const numbers = '23456789';
  const symbols = '!@#$%&*';
  const allChars = uppercase + lowercase + numbers + symbols;

  const randomBytes = new Uint32Array(length);
  window.crypto.getRandomValues(randomBytes);

  const pwdChars = [
    uppercase[randomBytes[0] % uppercase.length],
    lowercase[randomBytes[1] % lowercase.length],
    numbers[randomBytes[2] % numbers.length],
    symbols[randomBytes[3] % symbols.length],
  ];

  for (let i = 4; i < length; i++) {
    pwdChars.push(allChars[randomBytes[i] % allChars.length]);
  }

  const shuffleBytes = new Uint32Array(length);
  window.crypto.getRandomValues(shuffleBytes);
  for (let i = pwdChars.length - 1; i > 0; i--) {
    const j = shuffleBytes[i] % (i + 1);
    [pwdChars[i], pwdChars[j]] = [pwdChars[j], pwdChars[i]];
  }

  return pwdChars.join('');
}

export default function UserManagement() {
  const { salesPoints } = useAppContext();
  const [users, setUsers] = useState<UserItem[]>([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'catalog_manager' | 'designer'>('designer');
  const [salesPoint, setSalesPoint] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [createdUserCredentials, setCreatedUserCredentials] = useState<{ name: string; email: string; tempPassword: string; type?: 'created' | 'reset' } | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const excelInputRef = useRef<HTMLInputElement>(null);
  const [bulkProgress, setBulkProgress] = useState<{ total: number; current: number; successCount: number; failCount: number } | null>(null);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<'create' | 'list'>('list');
  const [searchQuery, setSearchQuery] = useState('');

  const handleSortClick = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase('tr');
    if (!query) return users;
    return users.filter(u => u.name.toLocaleLowerCase('tr').includes(query));
  }, [users, searchQuery]);

  const sortedUsers = useMemo(() => {
    const getSortValue = (u: UserItem): string => {
      if (sortColumn === 'role') return ROLE_LABELS[u.role] || u.role;
      if (sortColumn === 'assignedSalesPoint') return u.assignedSalesPoint || '';
      if (sortColumn === 'phone') return u.phone || '';
      return u[sortColumn] || '';
    };
    const sorted = [...filteredUsers].sort((a, b) =>
      getSortValue(a).localeCompare(getSortValue(b), 'tr', { sensitivity: 'base' })
    );
    if (sortDirection === 'desc') sorted.reverse();
    return sorted;
  }, [filteredUsers, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / USERS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const paginatedUsers = sortedUsers.slice(
    (currentPage - 1) * USERS_PER_PAGE,
    currentPage * USERS_PER_PAGE
  );

  const handleStartEditName = (u: UserItem) => {
    setEditingUserId(u.id);
    setEditingName(u.name);
  };

  const handleSaveUserName = async (userId: string) => {
    if (!editingName.trim()) return;
    try {
      setLoading(true);
      await updateDoc(doc(db, 'users', userId), {
        name: editingName.trim(),
      });
      setSuccess(`Kullanıcı ismi "${editingName.trim()}" olarak güncellendi.`);
      setEditingUserId(null);
    } catch (err: any) {
      console.error("İsim güncellenirken hata:", err);
      setError("İsim güncellenirken bir hata oluştu: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSalesPoint = async (userId: string, newSalesPoint: string) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        assignedSalesPoint: newSalesPoint,
      });
      setSuccess(newSalesPoint ? `Satış noktası "${newSalesPoint}" olarak atandı.` : 'Satış noktası ataması kaldırıldı.');
    } catch (err: any) {
      console.error("Satış noktası güncellenirken hata:", err);
      setError("Satış noktası güncellenirken bir hata oluştu: " + err.message);
    }
  };

  const handleUpdatePhone = async (userId: string, newPhone: string) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        phone: newPhone,
      });
      setSuccess(newPhone ? `Telefon numarası "${newPhone}" olarak güncellendi.` : 'Telefon numarası temizlendi; satış noktasının numarası kullanılacak.');
    } catch (err: any) {
      console.error("Telefon güncellenirken hata:", err);
      setError("Telefon güncellenirken bir hata oluştu: " + err.message);
    }
  };

  /** The phone that will actually appear on this user's proposal covers: their own
   *  number when set, otherwise the assigned sales point's number. */
  const salesPointPhoneOf = (salesPointName?: string): string =>
    salesPoints.find(sp => sp.name === salesPointName)?.phone || '';

  /** Keeps the create-form phone in sync with the picked sales point, without
   *  clobbering a number the admin typed by hand. */
  const handleSalesPointChange = (newSalesPoint: string) => {
    const previousDefault = salesPointPhoneOf(salesPoint);
    setSalesPoint(newSalesPoint);
    setPhone(prev => (!prev.trim() || prev === previousDefault ? salesPointPhoneOf(newSalesPoint) : prev));
  };

  const handleExcelImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError(null);
    setSuccess(null);
    setBulkProgress(null);

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const data = evt.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json<any>(sheet);

        if (rows.length === 0) {
          setError("Excel dosyasında hiç veri bulunamadı.");
          setLoading(false);
          return;
        }

        const { rows: validRows, hasRequiredColumns, unmatchedSalesPoints } = parseUserImportRows(rows, salesPoints);

        if (!hasRequiredColumns) {
          setError("Excel dosyasında 'Ad Soyad' ve 'E-posta' sütunları bulunmalıdır.");
          setLoading(false);
          return;
        }

        if (validRows.length === 0) {
          setError("Excel dosyasında geçerli isim ve e-posta içeren satır bulunamadı.");
          setLoading(false);
          return;
        }

        setBulkProgress({ total: validRows.length, current: 0, successCount: 0, failCount: 0 });

        let processed = 0;
        let success = 0;
        let fail = 0;

        for (const userRow of validRows) {
          let tempApp;
          try {
            const tempPassword = generateSecurePassword();
            const appName = `TempBulkApp_${Date.now()}_${processed}_${crypto.randomUUID().substring(0, 8)}`;
            tempApp = initializeApp(firebaseConfig, appName);
            const tempAuth = initializeAuth(tempApp, {
              persistence: inMemoryPersistence
            });

            const userCredential = await createUserWithEmailAndPassword(tempAuth, userRow.email, tempPassword);
            const uid = userCredential.user.uid;

            await signOut(tempAuth);

            if (db) {
              await setDoc(doc(db, 'users', uid), {
                name: userRow.name,
                email: userRow.email,
                role: userRow.role,
                phone: userRow.phone,
                assignedSalesPoint: userRow.assignedSalesPoint,
                createdAt: new Date().toISOString()
              });
            }

            try {
              await sendPasswordResetEmail(auth, userRow.email, {
                url: window.location.origin,
                handleCodeInApp: true,
              });
            } catch (mailErr: any) {
              if (mailErr.code === 'auth/unauthorized-domain') {
                console.warn("Domain not yet authorized in Firebase. Retrying default email reset...");
                try { await sendPasswordResetEmail(auth, userRow.email); } catch (e) { console.error("Fallback reset email failed:", e); }
              } else {
                console.error("Auto reset email failed for:", userRow.email, mailErr);
              }
            }

            success++;
          } catch (err: any) {
            console.error("Failed to add user:", userRow.email, err);
            fail++;
          } finally {
            if (tempApp) {
              deleteApp(tempApp).catch(e => console.error("Error deleting temp bulk app:", e));
            }
          }

          processed++;
          setBulkProgress({ total: validRows.length, current: processed, successCount: success, failCount: fail });
        }

        const unmatchedNote = unmatchedSalesPoints.length > 0
          ? ` Eşleşmeyen satış noktaları (atama yapılmadı): ${unmatchedSalesPoints.join(', ')}.`
          : '';
        setSuccess(`Toplu yükleme tamamlandı! Başarılı: ${success}, Başarısız/Zaten Kayıtlı: ${fail}.${unmatchedNote}`);
        setBulkProgress(null);
      } catch (err: any) {
        console.error("Bulk upload error:", err);
        setError("Excel işlenirken hata oluştu: " + err.message);
      } finally {
        setLoading(false);
        if (excelInputRef.current) excelInputRef.current.value = '';
      }
    };

    reader.onerror = () => {
      setError("Excel dosyası okunamadı.");
      setLoading(false);
    };

    reader.readAsBinaryString(file);
  };

  // Fetch users list
  useEffect(() => {
    if (!db) return;
    const usersCol = collection(db, 'users');
    const unsubscribe = onSnapshot(usersCol, (snapshot) => {
      const list: UserItem[] = [];
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        list.push({
          id: docSnap.id,
          name: data.name || '-',
          email: data.email || '-',
          role: data.role || 'designer',
          disabled: !!data.disabled,
          loginCount: data.loginCount || 0,
          lastLoginAt: data.lastLoginAt,
          assignedSalesPoint: data.assignedSalesPoint || '',
          phone: data.phone || ''
        });
      });
      setUsers(list);
    }, (err) => {
      console.error("Error loading users:", err);
    });

    return () => unsubscribe();
  }, []);

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!db) return;
    if (userId === auth.currentUser?.uid) {
      alert("Kendi hesabınızı silemezsiniz!");
      return;
    }
    if (!window.confirm(`'${userName}' isimli kullanıcıyı tamamen silmek istediğinize emin misiniz? Bu işlem geri alınamaz ve kullanıcının erişimi anında sonlandırılacaktır.`)) {
      return;
    }
    setError(null);
    setSuccess(null);
    try {
      await deleteDoc(doc(db, 'users', userId));
      setSuccess(`'${userName}' isimli kullanıcı başarıyla silindi.`);
    } catch (err: any) {
      console.error("Failed to delete user document:", err);
      setError("Kullanıcı silinirken hata oluştu: " + err.message);
    }
  };

  const handleUpdateUserRole = async (userId: string, userName: string, newRole: 'admin' | 'catalog_manager' | 'designer') => {
    if (!db) return;
    if (userId === auth.currentUser?.uid) {
      alert("Kendi rolünüzü değiştiremezsiniz!");
      return;
    }
    const roleText = newRole === 'admin' ? 'Yönetici' : newRole === 'catalog_manager' ? 'Katalog Yöneticisi' : 'Tasarımcı';
    if (!window.confirm(`'${userName}' isimli kullanıcının rolünü '${roleText}' olarak güncellemek istediğinize emin misiniz?`)) {
      return;
    }
    setError(null);
    setSuccess(null);
    try {
      await updateDoc(doc(db, 'users', userId), {
        role: newRole
      });
      setSuccess(`'${userName}' kullanıcısının rolü başarıyla '${roleText}' olarak güncellendi.`);
    } catch (err: any) {
      console.error("Failed to update user role:", err);
      setError("Rol güncellenirken hata oluştu: " + err.message);
    }
  };

  const handleToggleDeactivateUser = async (userId: string, userName: string, isCurrentlyDisabled: boolean) => {
    if (!db) return;
    if (userId === auth.currentUser?.uid) {
      alert("Kendi hesabınızı pasifleştiremezsiniz!");
      return;
    }
    const actionText = isCurrentlyDisabled ? 'aktif hale getirmek' : 'pasif (deaktif) hale getirmek';
    if (!window.confirm(`'${userName}' isimli kullanıcıyı ${actionText} istediğinize emin misiniz?`)) {
      return;
    }
    setError(null);
    setSuccess(null);
    try {
      await updateDoc(doc(db, 'users', userId), {
        disabled: !isCurrentlyDisabled
      });
      setSuccess(`'${userName}' isimli kullanıcı başarıyla ${isCurrentlyDisabled ? 'aktif' : 'pasif'} hale getirildi.`);
    } catch (err: any) {
      console.error("Failed to update user status:", err);
      setError("Kullanıcı durumu güncellenirken hata oluştu: " + err.message);
    }
  };

  const handleSendResetEmail = async (userEmail: string, userName: string) => {
    setError(null);
    setSuccess(null);
    try {
      try {
        await sendPasswordResetEmail(auth, userEmail, {
          url: window.location.origin,
          handleCodeInApp: true,
        });
      } catch (domainErr: any) {
        if (domainErr.code === 'auth/unauthorized-domain') {
          console.warn("Domain not yet authorized in Firebase Console. Using default reset link...");
          await sendPasswordResetEmail(auth, userEmail);
        } else {
          throw domainErr;
        }
      }
      setSuccess(`'${userName}' için şifre sıfırlama e-postası başarıyla gönderildi.`);
      setCreatedUserCredentials({
        name: userName,
        email: userEmail,
        tempPassword: 'E-Posta Sıfırlama Bağlantısı İletildi',
        type: 'reset',
      });
    } catch (err: any) {
      console.error("Failed to send reset email:", err);
      setError("Şifre sıfırlama e-postası gönderilirken hata oluştu: " + err.message);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError('Lütfen tüm alanları doldurun.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    let tempApp: any = null;
    try {
      // 1. Initialize a secondary Firebase App with inMemoryPersistence
      // This prevents IndexedDB locks and prevents Admin user from being logged out
      const appName = `TempApp_${Date.now()}_${crypto.randomUUID().substring(0, 8)}`;
      tempApp = initializeApp(firebaseConfig, appName);
      const tempAuth = initializeAuth(tempApp, {
        persistence: inMemoryPersistence
      });

      // 2. Create the user in Firebase Auth with a cryptographically secure auto-generated temporary password
      const autoPassword = generateSecurePassword();
      const userCredential = await createUserWithEmailAndPassword(tempAuth, email.trim(), autoPassword);
      const uid = userCredential.user.uid;

      // 3. Log out from temporary auth instance
      await signOut(tempAuth);

      // 4. Save the user metadata and role in Firestore
      if (db) {
        await setDoc(doc(db, 'users', uid), {
          name: name.trim(),
          email: email.trim().toLowerCase(),
          role: role,
          assignedSalesPoint: salesPoint || '',
          phone: phone.trim(),
          createdAt: new Date().toISOString()
        });
      }

      // 5. Send welcome / password reset email with smart domain fallback
      try {
        await sendPasswordResetEmail(auth, email.trim(), {
          url: window.location.origin,
          handleCodeInApp: true,
        });
      } catch (domainErr: any) {
        if (domainErr.code === 'auth/unauthorized-domain') {
          console.warn("Domain not authorized in Firebase yet. Using default password reset link...");
          try { await sendPasswordResetEmail(auth, email.trim()); } catch (e) { console.error("Fallback email failed:", e); }
        } else {
          console.error("Failed to send welcome email automatically:", domainErr);
        }
      }

      const roleLabel = role === 'admin' ? 'Yönetici' : role === 'catalog_manager' ? 'Katalog Yöneticisi' : 'Tasarımcı';
      setSuccess(`'${name}' başarıyla '${roleLabel}' olarak eklendi! Şifre belirleme e-postası otomatik olarak gönderildi.`);
      
      setCreatedUserCredentials({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        tempPassword: autoPassword,
        type: 'created',
      });

      setName('');
      setEmail('');
      setRole('designer');
      setSalesPoint('');
      setPhone('');
    } catch (err: any) {
      console.error("User creation error:", err);
      if (err.code === 'auth/email-already-in-use') {
        setError('Bu e-posta adresi zaten kullanımda.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Geçersiz bir e-posta adresi girdiniz.');
      } else {
        setError('Kullanıcı oluşturulurken bir hata oluştu: ' + (err.message || err.code));
      }
    } finally {
      setLoading(false);
      if (tempApp) {
        deleteApp(tempApp).catch(e => console.error("Error deleting temp app:", e));
      }
    }
  };

  return (
    <div className="p-4 space-y-6">
      <div className="flex justify-center">
        <FluidTabs
          activeId={activeTab}
          onChange={(id) => setActiveTab(id as 'create' | 'list')}
          tabs={[
            { id: 'create', label: 'Kullanıcı Tanımla', icon: <UserPlus className="w-3.5 h-3.5" /> },
            { id: 'list', label: 'Sistem Kullanıcıları', icon: <Shield className="w-3.5 h-3.5" /> },
          ]}
        />
      </div>

      {activeTab === 'create' && (
      <div className="w-full md:w-[32rem] mx-auto bg-white border border-stone-200 rounded-2xl p-6 shadow-xs h-fit">
        <div className="flex items-center space-x-2 border-b border-stone-100 pb-3 mb-5">
          <UserPlus className="w-4 h-4 text-brand-600" />
          <h2 className="text-xs uppercase tracking-wider font-bold text-stone-900">Kullanıcı Tanımla</h2>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border-l-2 border-red-500 rounded text-red-700 text-xs flex items-start space-x-1.5">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-emerald-50 border-l-2 border-emerald-500 rounded text-emerald-700 text-xs flex items-start space-x-1.5">
            <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>{success}</span>
          </div>
        )}

        <form onSubmit={handleCreateUser} className="space-y-5">
          <div>
            <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">
              İsim Soyisim
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-stone-400">
                <User className="w-3.5 h-3.5" />
              </span>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ahmet Yılmaz"
                className="w-full pl-9 pr-4 py-2.5 text-xs bg-stone-50/50 border border-stone-200 rounded-xl focus:border-brand-500 focus:outline-none transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">
              E-posta Adresi
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-stone-400">
                <Mail className="w-3.5 h-3.5" />
              </span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ahmet@eczacibasi.com.tr"
                className="w-full pl-9 pr-4 py-2.5 text-xs bg-stone-50/50 border border-stone-200 rounded-xl focus:border-brand-500 focus:outline-none transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">
              Kullanıcı Rolü
            </label>
            <div className="flex flex-col space-y-2 sm:space-y-0 sm:grid sm:grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setRole('designer')}
                className={`py-2.5 px-1 text-[11px] font-bold rounded-xl border flex items-center justify-center space-x-1 transition-colors ${
                  role === 'designer'
                    ? 'border-[#945f46] bg-[#945f46]/5 text-[#945f46]'
                    : 'border-stone-200 hover:bg-stone-50 text-stone-500'
                }`}
              >
                <User className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">Tasarımcı</span>
              </button>
              <button
                type="button"
                onClick={() => setRole('catalog_manager')}
                className={`py-2.5 px-1 text-[11px] font-bold rounded-xl border flex items-center justify-center space-x-1 transition-colors ${
                  role === 'catalog_manager'
                    ? 'border-[#945f46] bg-[#945f46]/5 text-[#945f46]'
                    : 'border-stone-200 hover:bg-stone-50 text-stone-500'
                }`}
                title="Sadece Ürün Kataloğu ve Matris yönetimi yapabilir"
              >
                <FileSpreadsheet className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">Katalog Yön.</span>
              </button>
              <button
                type="button"
                onClick={() => setRole('admin')}
                className={`py-2.5 px-1 text-[11px] font-bold rounded-xl border flex items-center justify-center space-x-1 transition-colors ${
                  role === 'admin'
                    ? 'border-[#945f46] bg-[#945f46]/5 text-[#945f46]'
                    : 'border-stone-200 hover:bg-stone-50 text-stone-500'
                }`}
              >
                <Shield className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">Yönetici</span>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">
              Atanmış Satış Noktası
            </label>
            <select
              value={salesPoint}
              onChange={(e) => handleSalesPointChange(e.target.value)}
              className="w-full px-3 py-2.5 text-xs bg-stone-50/50 border border-stone-200 rounded-xl focus:border-brand-500 focus:outline-none transition-colors text-stone-700"
            >
              <option value="">Satış Noktası Yok</option>
              {salesPoints.map(store => (
                <option key={store.id || store.name} value={store.name}>{store.name}</option>
              ))}
            </select>
            <p className="mt-1 text-[9px] text-stone-400 leading-relaxed">
              Kullanıcı teklif oluştururken bu satış noktası otomatik seçili gelir.
            </p>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">
              Telefon
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={salesPointPhoneOf(salesPoint) || '(0212) 000 00 00'}
              className="w-full px-3 py-2.5 text-xs bg-stone-50/50 border border-stone-200 rounded-xl focus:border-brand-500 focus:outline-none transition-colors text-stone-700"
            />
            <p className="mt-1 text-[9px] text-stone-400 leading-relaxed">
              Satış noktası seçildiğinde o noktanın numarası otomatik gelir; dilediğiniz numarayı yazabilirsiniz. Bu numara teklif kapağında görünür.
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#945f46] hover:bg-[#83523c] text-white text-xs font-bold py-2.5 px-4 rounded-xl shadow-xs transition-colors flex items-center justify-center space-x-1.5 tracking-wider uppercase disabled:opacity-75 disabled:cursor-not-allowed mt-4"
          >
            {loading ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Oluşturuluyor...</span>
              </>
            ) : (
              <>
                <UserPlus className="w-3.5 h-3.5" />
                <span>Kullanıcı Oluştur</span>
              </>
            )}
          </button>
        </form>

        {bulkProgress && (
          <div className="mt-4 p-3 bg-stone-50 border border-stone-200 rounded-xl space-y-2">
            <div className="flex justify-between text-[10px] font-bold text-stone-500 uppercase">
              <span>Toplu Yükleme İlerlemesi</span>
              <span>{bulkProgress.current} / {bulkProgress.total}</span>
            </div>
            <div className="w-full bg-stone-200 rounded-full h-1.5 overflow-hidden">
              <div 
                className="bg-[#945f46] h-1.5 transition-all duration-300" 
                style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
              />
            </div>
            <div className="flex justify-between text-[9px] text-stone-400 font-semibold">
              <span>Başarılı: {bulkProgress.successCount}</span>
              <span>Başarısız: {bulkProgress.failCount}</span>
            </div>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-stone-150 space-y-2">
          <button
            type="button"
            disabled={loading}
            onClick={() => excelInputRef.current?.click()}
            className="w-full py-2.5 px-3 text-xs bg-stone-100 hover:bg-stone-200 text-stone-700 font-bold rounded-xl flex items-center justify-center space-x-1.5 transition-colors border border-stone-200 disabled:opacity-50"
          >
            <FileSpreadsheet className="w-3.5 h-3.5 text-stone-500" />
            <span>Excel ile Toplu Yükle</span>
          </button>
          <input
            type="file"
            ref={excelInputRef}
            accept=".xlsx, .xls"
            className="hidden"
            onChange={handleExcelImport}
          />
          <p className="text-[9px] text-stone-400 text-center leading-relaxed">
            Excel dosyasında en az <strong>Ad Soyad</strong> ve <strong>E-posta</strong> sütunları bulunmalıdır. <strong>Yetki</strong>, <strong>Telefon</strong> ve <strong>Satış Noktası</strong> sütunları isteğe bağlıdır. Satış noktası adı sistemdeki kayıtla eşleşmelidir (eşleşmeyenler atanmadan yüklenir ve sonuç mesajında listelenir); telefon boş bırakılırsa satış noktasının numarası yazılır.
          </p>
        </div>
      </div>
      )}

      {/* Right: Users List */}
      {activeTab === 'list' && (
      <div className="w-full bg-white border border-stone-200 rounded-2xl p-5 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-stone-100 pb-3 mb-4">
          <div className="flex items-center space-x-2">
            <Shield className="w-4 h-4 text-brand-600" />
            <h2 className="text-xs uppercase tracking-wider font-bold text-stone-900">
              Sistem Kullanıcıları ({filteredUsers.length}{searchQuery.trim() ? ` / ${users.length}` : ''})
            </h2>
          </div>
          <div className="relative w-full sm:w-64">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-stone-400">
              <Search className="w-3.5 h-3.5" />
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Kullanıcı adına göre ara..."
              className="w-full pl-9 pr-3 py-1.5 text-xs bg-stone-50/50 border border-stone-200 rounded-xl focus:border-brand-500 focus:outline-none transition-colors placeholder:text-stone-400 placeholder:italic"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-stone-200 text-stone-400 text-[10px] uppercase font-bold tracking-wider">
                <th className="py-1.5 px-3">
                  <button type="button" onClick={() => handleSortClick('name')} className="flex items-center space-x-1 hover:text-stone-700 transition-colors whitespace-nowrap">
                    <span>İsim Soyisim</span>
                    {sortColumn === 'name' ? (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                  </button>
                </th>
                <th className="py-1.5 px-3 w-64">
                  <button type="button" onClick={() => handleSortClick('email')} className="flex items-center space-x-1 hover:text-stone-700 transition-colors">
                    <span>E-posta</span>
                    {sortColumn === 'email' ? (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                  </button>
                </th>
                <th className="py-1.5 px-3 w-20">
                  <button type="button" onClick={() => handleSortClick('role')} className="flex items-center space-x-1 hover:text-stone-700 transition-colors">
                    <span>Rol</span>
                    {sortColumn === 'role' ? (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                  </button>
                </th>
                <th className="py-1.5 px-3 w-44">
                  <button type="button" onClick={() => handleSortClick('assignedSalesPoint')} className="flex items-center space-x-1 hover:text-stone-700 transition-colors">
                    <span>Satış Noktası</span>
                    {sortColumn === 'assignedSalesPoint' ? (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                  </button>
                </th>
                <th className="py-1.5 px-3 w-36">
                  <button type="button" onClick={() => handleSortClick('phone')} className="flex items-center space-x-1 hover:text-stone-700 transition-colors">
                    <span>Telefon</span>
                    {sortColumn === 'phone' ? (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                  </button>
                </th>
                <th className="py-1.5 px-3 w-36 text-right">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 font-medium text-stone-700">
              {paginatedUsers.map((u) => {
                const isSelf = u.id === auth.currentUser?.uid;
                return (
                  <tr key={u.id} className={`hover:bg-stone-50/30 transition-colors ${u.disabled ? 'bg-stone-50/40 text-stone-400' : ''}`}>
                    <td className={`py-1.5 px-3 font-bold ${u.disabled ? 'line-through text-stone-400' : 'text-stone-900'}`}>
                      {editingUserId === u.id ? (
                        <div className="flex items-center space-x-1">
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveUserName(u.id);
                              if (e.key === 'Escape') setEditingUserId(null);
                            }}
                            autoFocus
                            className="px-2 py-1 text-xs border border-brand-400 rounded focus:outline-none focus:ring-1 focus:ring-brand-500 bg-white"
                          />
                          <button
                            type="button"
                            onClick={() => handleSaveUserName(u.id)}
                            className="p-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded shadow-2xs"
                            title="Kaydet"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingUserId(null)}
                            className="p-1 bg-stone-200 hover:bg-stone-300 text-stone-700 rounded"
                            title="İptal"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <div 
                            className="w-6 h-6 rounded-full text-white font-bold text-[9.5px] flex items-center justify-center shrink-0 uppercase shadow-2xs"
                            style={{ backgroundColor: getUserAvatarColor(u.name, u.email) }}
                          >
                            {getUserInitials(u.name, u.email)}
                          </div>
                          <span className="whitespace-nowrap">{u.name}</span>
                          {isSelf && <span className="text-[9px] text-[#945f46] font-semibold lowercase bg-[#945f46]/5 px-1.5 py-0.5 rounded whitespace-nowrap">Siz</span>}
                        </div>
                      )}
                      <div className="mt-0.5">
                        {(u.loginCount || 0) > 0 || !!u.lastLoginAt ? (
                          <span className="inline-flex items-center space-x-1 text-[8.5px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200/60 whitespace-nowrap">
                            <CheckCircle className="w-2.5 h-2.5 text-emerald-600 shrink-0" />
                            <span>Aktif (Şifre Belirlendi)</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center space-x-1 text-[8.5px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200/60 whitespace-nowrap">
                            <Clock className="w-2.5 h-2.5 text-amber-600 shrink-0" />
                            <span>Davet Edildi (Şifre Bekleniyor)</span>
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={`py-1.5 px-3 truncate max-w-64 ${u.disabled ? 'line-through text-stone-400' : ''}`} title={u.email}>{u.email}</td>
                    <td className="py-1.5 px-3">
                      {isSelf ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-250/50">
                          Yönetici
                        </span>
                      ) : (
                        <select
                          value={u.role}
                          disabled={u.disabled}
                          onChange={(e) => handleUpdateUserRole(u.id, u.name, e.target.value as 'admin' | 'catalog_manager' | 'designer')}
                          className="bg-stone-50 border border-stone-200 rounded px-2 py-0.5 text-[10px] font-bold text-stone-700 focus:outline-none focus:border-brand-500 uppercase cursor-pointer"
                        >
                          <option value="designer">Tasarımcı</option>
                          <option value="catalog_manager">Katalog Yöneticisi</option>
                          <option value="admin">Yönetici</option>
                        </select>
                      )}
                    </td>
                    <td className="py-1.5 px-3">
                      <select
                        value={u.assignedSalesPoint || ''}
                        disabled={u.disabled}
                        onChange={(e) => handleUpdateSalesPoint(u.id, e.target.value)}
                        className="bg-stone-50 border border-stone-200 rounded px-2 py-0.5 text-[10px] font-bold text-stone-700 focus:outline-none focus:border-brand-500 cursor-pointer max-w-[10.5rem]"
                      >
                        <option value="">Atanmadı</option>
                        {salesPoints.map(store => (
                          <option key={store.id || store.name} value={store.name}>{store.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1.5 px-3">
                      {/* Uncontrolled on purpose: the row re-mounts (see key) whenever the
                          stored number changes, so typing is never fought by the snapshot. */}
                      <input
                        key={`phone-${u.id}-${u.phone || ''}`}
                        type="tel"
                        defaultValue={u.phone || ''}
                        disabled={u.disabled}
                        placeholder={salesPointPhoneOf(u.assignedSalesPoint) || 'Numara yok'}
                        title="Boş bırakılırsa satış noktasının telefonu kullanılır."
                        onBlur={(e) => {
                          const value = e.target.value.trim();
                          if (value !== (u.phone || '')) handleUpdatePhone(u.id, value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.currentTarget.blur();
                          if (e.key === 'Escape') {
                            e.currentTarget.value = u.phone || '';
                            e.currentTarget.blur();
                          }
                        }}
                        className="w-full max-w-[8.5rem] bg-stone-50 border border-stone-200 rounded px-2 py-0.5 text-[10px] font-bold text-stone-700 focus:outline-none focus:border-brand-500 disabled:opacity-60"
                      />
                    </td>
                    <td className="py-1.5 px-3 text-right">
                      <div className="flex items-center justify-end space-x-1">
                        {/* Edit Name Button */}
                        <button
                          type="button"
                          onClick={() => handleStartEditName(u)}
                          title="İsim Soyisim Düzenle"
                          className="p-1 text-stone-400 hover:text-brand-700 hover:bg-stone-100 rounded transition-colors"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>

                        {/* Send Reset Email */}
                        <button
                          type="button"
                          onClick={() => handleSendResetEmail(u.email, u.name)}
                          title="Şifre Sıfırlama E-postası Gönder"
                          className="p-1 text-stone-400 hover:text-blue-600 hover:bg-stone-100 rounded transition-colors"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                        </button>

                        {/* Toggle Activate / Deactivate */}
                        <button
                          type="button"
                          disabled={isSelf}
                          onClick={() => handleToggleDeactivateUser(u.id, u.name, !!u.disabled)}
                          title={u.disabled ? "Hesabı Aktifleştir" : "Hesabı Devre Dışı Bırak"}
                          className={`p-1 rounded transition-colors ${
                            isSelf
                              ? 'text-stone-200 cursor-not-allowed'
                              : u.disabled
                                ? 'text-emerald-600 hover:bg-emerald-50'
                                : 'text-stone-400 hover:text-amber-600 hover:bg-stone-100'
                          }`}
                        >
                          {u.disabled ? <UserCheck className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                        </button>

                        {/* Delete User */}
                        <button
                          type="button"
                          disabled={isSelf}
                          onClick={() => handleDeleteUser(u.id, u.name)}
                          title="Kullanıcıyı Sil"
                          className={`p-1 rounded transition-colors ${
                            isSelf
                              ? 'text-stone-200 cursor-not-allowed'
                              : 'text-stone-400 hover:text-red-600 hover:bg-stone-100'
                          }`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-stone-400">
                    Kullanıcı listesi yükleniyor...
                  </td>
                </tr>
              )}
              {users.length > 0 && filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-stone-400">
                    "{searchQuery}" ile eşleşen kullanıcı bulunamadı.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filteredUsers.length > 0 && (
          <div className="flex items-center justify-between pt-4 mt-2 border-t border-stone-100">
            <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">
              Sayfa {currentPage} / {totalPages} · Toplam {filteredUsers.length} kullanıcı
            </span>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                className="flex items-center space-x-1 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-stone-600 bg-stone-50 border border-stone-200 rounded-lg hover:bg-stone-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-stone-50"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                <span>Geri</span>
              </button>
              <button
                type="button"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                className="flex items-center space-x-1 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-stone-600 bg-stone-50 border border-stone-200 rounded-lg hover:bg-stone-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-stone-50"
              >
                <span>İleri</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Created or Reset User Invite Credentials Modal */}
      {createdUserCredentials && (
        <div className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-stone-200 p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-stone-100 pb-3">
              <div className="flex items-center space-x-2 text-brand-700 font-extrabold text-sm">
                {createdUserCredentials.type === 'reset' ? (
                  <>
                    <RefreshCw className="w-5 h-5 text-brand-600 animate-spin-once" />
                    <span>Şifre Sıfırlama Talebi İletildi!</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <span>Kullanıcı Hesabı Oluşturuldu!</span>
                  </>
                )}
              </div>
              <button 
                type="button"
                onClick={() => setCreatedUserCredentials(null)}
                className="text-stone-400 hover:text-stone-600 p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-stone-600 leading-relaxed">
              {createdUserCredentials.type === 'reset' ? (
                <>
                  <strong>{createdUserCredentials.name}</strong> ({createdUserCredentials.email}) kullanıcısına şifre sıfırlama e-postası gönderildi. Kurumsal mail engellerine karşılık kullanıcının şifresini yenileyebilmesi için bilgilendirme metnini kopyalayıp Teams veya WhatsApp üzerinden iletebilirsiniz.
                </>
              ) : (
                <>
                  Kullanıcıya e-posta gönderildi. Kurumsal mail filtrelerine takılması durumuna karşılık kullanıcının <strong>geçici şifresi</strong> aşağıdadır:
                </>
              )}
            </p>

            <div className="bg-stone-50 p-4 rounded-xl border border-stone-200 space-y-2 font-mono text-xs">
              <div className="flex justify-between">
                <span className="text-stone-400 font-bold uppercase text-[9px]">E-Posta:</span>
                <span className="font-bold text-stone-800">{createdUserCredentials.email}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-stone-400 font-bold uppercase text-[9px]">
                  {createdUserCredentials.type === 'reset' ? 'Durum:' : 'Geçici Şifre:'}
                </span>
                <span className="font-bold text-brand-700 bg-brand-50 px-2 py-0.5 rounded border border-brand-200">
                  {createdUserCredentials.tempPassword}
                </span>
              </div>
            </div>

            <div className="pt-2 space-y-2">
              <button
                type="button"
                onClick={() => {
                  const inviteText = createdUserCredentials.type === 'reset'
                    ? `Merhaba ${createdUserCredentials.name},\n\nIntema Teklif Portalı hesabınız için şifre sıfırlama e-postası gönderilmiştir.\n\nGiriş ve Şifre Yenileme Adresi: ${window.location.origin}\nE-posta: ${createdUserCredentials.email}\n\nE-postanıza gelen bağlantıya tıklayarak veya giriş sayfasındaki 'Şifremi Unuttum' adımlarını takip ederek yeni şifrenizi belirleyebilirsiniz.`
                    : `Merhaba ${createdUserCredentials.name},\n\nIntema Teklif Portalı hesabınız tanımlanmıştır.\n\nGiriş Adresi: ${window.location.origin}\nE-posta: ${createdUserCredentials.email}\nGeçici Şifre: ${createdUserCredentials.tempPassword}\n\nSisteme ilk girişinizi yaptıktan sonra dilerseniz şifrenizi güncelleyebilirsiniz.`;
                  navigator.clipboard.writeText(inviteText);
                  setIsCopied(true);
                  setTimeout(() => setIsCopied(false), 3000);
                }}
                className="w-full py-2.5 px-4 bg-[#945f46] hover:bg-[#83523c] text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center justify-center space-x-2 uppercase tracking-wider"
              >
                {isCopied ? (
                  <>
                    <Check className="w-4 h-4 text-emerald-300" />
                    <span>Davet Metni Kopyalandı!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>
                      {createdUserCredentials.type === 'reset' ? 'Sıfırlama Metnini Kopyala' : 'Davet Bilgilerini & Şifreyi Kopyala'}
                    </span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => setCreatedUserCredentials(null)}
                className="w-full py-2 text-xs font-bold text-stone-500 hover:text-stone-800 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
